import React from "react"
import { useTranslation } from "react-i18next"
import {
  withFormik,
  FormikProps,
  FormikErrors,
  Form,
  Field,
  FieldProps,
  FormikHelpers as FormikActions,
} from "formik"
import { Grid } from "@mui/material"

import i18n from "config/i18n"
import { Card, Headline, Submit } from "components/Sessions/elements"
import Input from "components/ui/Input"
import {
  PERSONAL_CODE_LENGTH,
  PERSONAL_CODE_REGEX,
  PHONE_LENGTH,
  PHONE_REGEX,
} from "helpers/client"
import { ClientMobileIdFormValues } from "types"

interface FormProps {
  initialValues: ClientMobileIdFormValues
  onSubmit: (
    values: ClientMobileIdFormValues,
    actions: FormikActions<object>,
  ) => void
}

type Props = FormikProps<ClientMobileIdFormValues> & FormProps

const MobileIdForm = ({ values, errors, isSubmitting }: Props) => {
  const { t } = useTranslation()

  const { terms } = values
  const isDisabled = isSubmitting || terms === false

  return (
    <Form>
      <Card elevation={0}>
        <Headline>{t("providers.mobile_id.title")}</Headline>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Field name="personal_code">
              {({ field }: FieldProps) => (
                <Input
                  fullWidth
                  label={t("features.client.personal_code")}
                  helperText={errors.personal_code}
                  disabled={isSubmitting}
                  {...field}
                />
              )}
            </Field>
          </Grid>
          <Grid item xs={6}>
            <Field name="phone">
              {({ field }: FieldProps) => (
                <Input
                  fullWidth
                  label={t("features.client.phone")}
                  helperText={errors.phone}
                  disabled={isSubmitting}
                  {...field}
                />
              )}
            </Field>
          </Grid>
        </Grid>
      </Card>
      <Submit type="submit" disabled={isDisabled}>
        {t("pages.sessions.form.submit")}
      </Submit>
    </Form>
  )
}

const MobileIdFormWithFormik = withFormik<FormProps, ClientMobileIdFormValues>({
  enableReinitialize: true,
  mapPropsToValues: ({ initialValues }) => initialValues,
  handleSubmit: (values, actions) => actions.props.onSubmit(values, actions),
  validate: (values: ClientMobileIdFormValues) => {
    const errors: FormikErrors<ClientMobileIdFormValues> = {}
    const { personal_code, phone } = values

    if (!personal_code) {
      errors.personal_code = i18n.t("validation.field.required", {
        field: i18n.t("features.client.personal_code"),
      })
    } else if (
      !PERSONAL_CODE_REGEX.test(personal_code) ||
      personal_code.length != PERSONAL_CODE_LENGTH
    ) {
      errors.personal_code = i18n.t("validation.field.invalid", {
        field: i18n.t("features.client.personal_code"),
      })
    }

    if (!phone) {
      errors.phone = i18n.t("validation.field.required", {
        field: i18n.t("features.client.phone"),
      })
    } else if (!PHONE_REGEX.test(phone) || phone.length != PHONE_LENGTH) {
      errors.phone = i18n.t("validation.field.invalid", {
        field: i18n.t("features.client.phone"),
      })
    }

    return errors
  },
})(MobileIdForm)

export default MobileIdFormWithFormik
